
import React, { useState, useEffect } from 'react';
import { Icons } from '../ui/Icons';
import NightAuditView from './NightAuditView';
import DailyTasksView from './DailyTasksView';
import { database, subscribeToPath } from '../../services/database';
import { TaskCategory, StaffMember } from '../../types';

interface TaskManagerViewProps {
    staffName?: string;
    staffList?: StaffMember[];
    isAdmin?: boolean;
}

type TaskSubPage = 'hub' | 'nightAudit' | 'dailyTasks';

const TaskManagerView: React.FC<TaskManagerViewProps> = ({ staffName = 'Admin', staffList = [], isAdmin = false }) => {
    const [activeSubPage, setActiveSubPage] = useState<TaskSubPage>('hub');
    const [categories, setCategories] = useState<{ [id: string]: TaskCategory }>({});
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setIsLoading(true);
        const unsub = subscribeToPath('taskCategories', (data) => {
            setCategories(data || {});
            setIsLoading(false);
        });
        return () => unsub();
    }, []);

    const categoriesList = Object.values(categories).sort((a, b) => a.name.localeCompare(b.name));

    if (activeSubPage === 'nightAudit') {
        return (
            <NightAuditView 
                onBack={() => {
                    setActiveSubPage('hub');
                    setSelectedCategoryId(null);
                }} 
                staffName={staffName}
                staffList={staffList}
            />
        );
    }

    if (activeSubPage === 'dailyTasks') {
        return (
            <DailyTasksView 
                onBack={() => {
                    setActiveSubPage('hub');
                    setSelectedCategoryId(null);
                }} 
                staffName={staffName}
                staffList={staffList}
                isAdmin={isAdmin}
                initialCategoryId={selectedCategoryId}
                onOpenNightAudit={() => setActiveSubPage('nightAudit')}
            />
        );
    }

    const openCategory = (id: string) => {
        setSelectedCategoryId(id);
        setActiveSubPage('dailyTasks');
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-8">
                <h1 className="text-3xl font-normal text-gray-900 dark:text-white uppercase tracking-tight mb-2">Task Hub</h1>
                <p className="text-gray-500 dark:text-gray-400 font-normal">Select a task category to manage procedures and reports.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {isLoading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="bg-white dark:bg-slate-800 p-8 rounded-3xl animate-pulse h-48 border border-gray-100 dark:border-slate-700" />
                    ))
                ) : (
                    <>
                        {categoriesList.map(category => (
                            <button 
                                key={category.id}
                                onClick={() => openCategory(category.id)}
                                className="group relative bg-white dark:bg-slate-800 p-8 rounded-3xl shadow-sm hover:shadow-xl border border-gray-100 dark:border-slate-700 transition-all duration-300 text-left overflow-hidden"
                            >
                                <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                                    <Icons.ClipboardList className="w-24 h-24" />
                                </div>
                                <div className="relative z-10">
                                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: `${category.color}15`, color: category.color }}>
                                        <Icons.ClipboardList className="w-7 h-7" />
                                    </div>
                                    <h3 className="text-xl font-normal text-gray-900 dark:text-white uppercase tracking-tight mb-2">{category.name}</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 font-normal leading-relaxed">
                                        Manage daily procedures and checklists for {category.name}.
                                    </p>
                                    <div className="mt-6 flex items-center gap-2 font-normal text-sm opacity-0 group-hover:opacity-100 transition-all transform translate-x-[-10px] group-hover:translate-x-0" style={{ color: category.color }}>
                                        Open Tasks <Icons.ArrowRight className="w-4 h-4" />
                                    </div>
                                </div>
                            </button>
                        ))}

                        {categoriesList.length === 0 && (
                            <div className="sm:col-span-2 lg:col-span-3 py-16 px-4 bg-gray-50 dark:bg-slate-900/50 rounded-3xl border-2 border-dashed border-gray-200 dark:border-slate-800 flex flex-col items-center justify-center text-center">
                                <div className="w-16 h-16 bg-white dark:bg-slate-800 rounded-2xl flex items-center justify-center text-gray-300 mb-4 shadow-sm">
                                    <Icons.Settings className="w-8 h-8" />
                                </div>
                                <h3 className="text-lg font-normal text-gray-900 dark:text-white uppercase tracking-tight mb-2">No Categories Configured</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400 font-normal max-w-sm mb-6">
                                    Admins must configure task categories in the Settings panel before tasks can be managed.
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default TaskManagerView;
